import { encodePacked, type PublicClient } from 'viem';
import { SupportedChains, type THexString, CHAIN_CONFIG, type IPoolState, type IQuoteResult, type TTokenInfo, FeeAmount, getSupportedChain } from './types';

import V3FactoryABI from './abi/UniswapV3Factory.json';
import V3PoolABI from './abi/V3Pool.json';
import QuoterV2ABI from './abi/UniswapV3QuoterV2.json';
import QuoterV4ABI from './abi/UniswapV4Quoter.json';
import { getTokenInfo } from './token';
import { sleep } from '../utils';
let lastRequest = 0

// Define the pool types
export enum PoolType {
  V3 = 'v3',
  V4 = 'v4'
}

// Define the fee tiers to check for V3 pools
const V3_FEE_TIERS = [
  FeeAmount.LOWEST,  // 0.01%
  FeeAmount.LOW,     // 0.05%
  FeeAmount.MEDIUM,  // 0.3%
  FeeAmount.HIGH     // 1%
];

// Define a swap hop in the path
interface ISwapHop {
  poolType: PoolType;
  tokenIn: THexString;
  tokenOut: THexString;
  fee?: number;       // For V3 pools
  poolAddress?: THexString;
  amountOutPerOneIn?: number; // Price ratio
  liquidity?: bigint;  // Pool liquidity
}

// Define the complete swap path
export interface ISwapPath {
  hops: ISwapHop[];
  totalOutputPerInput: number;
  estimatedGas: bigint;
  effectiveOutput?: number
}

/**
 * Get the V3 pool address for a token pair and fee
 */
async function getV3PoolAddress(
  tokenA: THexString,
  tokenB: THexString,
  fee: number,
  chain: SupportedChains,
  publicClient: PublicClient
): Promise<THexString | null> {
  const config = CHAIN_CONFIG[chain];

  try {
    // Sort tokens to match Uniswap's ordering
    const [token0, token1] = tokenA.toLowerCase() < tokenB.toLowerCase() 
      ? [tokenA, tokenB] 
      : [tokenB, tokenA];

    if (lastRequest > Date.now() - 110) {
      await sleep(110)
    }
    lastRequest = Date.now()

    // Get pool address from factory contract
    const poolAddress = await publicClient.readContract({
      address: config.factoryAddress as `0x${string}`,
      abi: V3FactoryABI,
      functionName: 'getPool',
      args: [token0, token1, fee]
    }) as `0x${string}`;

    // Return null if pool doesn't exist
    if (poolAddress === '0x0000000000000000000000000000000000000000') {
      return null;
    }

    return poolAddress;
  } catch (error) {
    console.error(`Error getting V3 pool address for ${tokenA}/${tokenB} with fee ${fee}:`, error);
    return null;
  }
}

/**
 * Get the pool state for a V3 pool
 */
async function getV3PoolState(
  poolAddress: THexString,
  publicClient: PublicClient
): Promise<IPoolState | null> {

  try {
    if (lastRequest > Date.now() - 110) {
      await sleep(110)
    }
    lastRequest = Date.now()

    const slot0 = await publicClient.readContract({
      address: poolAddress,
      abi: V3PoolABI,
      functionName: 'slot0'
    }) as [bigint, number, number, number, number, number, boolean];

    if (lastRequest > Date.now() - 110) {
      await sleep(110)
    }
    lastRequest = Date.now()

    const liquidity = await publicClient.readContract({
      address: poolAddress,
      abi: V3PoolABI,
      functionName: 'liquidity'
    }) as bigint;

    return {
      sqrtPriceX96: slot0[0],
      liquidity,
      tickCurrent: slot0[1]
    };
  } catch (error) {
    console.error(`Error getting pool state for ${poolAddress}:`, error);
    return null;
  }
}

/**
 * Get a quote for a V3 swap
 */
export async function getV3Quote(
  tokenIn: THexString,
  tokenOut: THexString,
  amountIn: number,
  fee: number,
  chain: SupportedChains,
  decimalsIn: number,
  decimalsOut: number,
  publicClient: PublicClient
): Promise<IQuoteResult | null> {
  const config = CHAIN_CONFIG[chain];
  
  try {
    // Create token instances
    // const tokenInInstance = new Token(
    //   config.id,
    //   tokenIn,
    //   decimalsIn 
    // );
    
    // const tokenOutInstance = new Token(
    //   config.id,
    //   tokenOut,
    //   decimalsOut
    // );
    
    // Convert amount to wei
    const amountInWei = BigInt(amountIn * (10 ** decimalsIn));
    
    if (lastRequest > Date.now() - 110) {
      await sleep(110)
    }
    lastRequest = Date.now()

    // Call the QuoterV2 contract to get the quote
    const data = await publicClient.readContract({
      address: config.quoterV3Address as `0x${string}`,
      abi: QuoterV2ABI,
      functionName: 'quoteExactInputSingle',
      args: [{
        tokenIn: tokenIn, 
        tokenOut: tokenOut, 
        amountIn: amountInWei, 
        fee: fee,
        sqrtPriceLimitX96: 0n // 0 for no price limit
      }]
    }) as [bigint, bigint, number, bigint];

    const [amountOutWei, , ticksCrossed, gasEstimate] = data;
    
    // Convert amountOut from wei to token units
    const amountOut = Number(amountOutWei) / (10 ** decimalsOut);
    
    return {
      amountIn,
      amountOut,
      tokenIn: tokenIn,
      tokenOut: tokenOut,
      fee,
      gasEstimate,
    };
  } catch (error) {
    console.error(`Error getting V3 quote for ${tokenIn}/${tokenOut} with fee ${fee}:`, error);
    return null;
  }
}

/**
 * Get a quote for a V4 swap
 */
async function getV4Quote(
  tokenIn: THexString,
  tokenOut: THexString,
  amountIn: number,
  chain: SupportedChains,
  decimalsIn: number,
  decimalsOut: number,
  publicClient: PublicClient
): Promise<IQuoteResult | null> {
  const config = CHAIN_CONFIG[chain];
  
  try {
    // Create token instances

    
    // Convert amount to wei
    const amountInWei = BigInt(amountIn * (10 ** decimalsIn));

    // Create the pool key for V4
    const poolKey = {
      currency0: tokenIn < tokenOut ? tokenIn : tokenOut,
      currency1: tokenIn < tokenOut ? tokenOut : tokenIn,
      fee: FeeAmount.MEDIUM, // Default to medium fee
      tickSpacing: 60, // Medium fee tier typically has 60 tick spacing
      hooks: '0x0000000000000000000000000000000000000000' as `0x${string}`
    };

    if (lastRequest > Date.now() - 110) {
      await sleep(110)
    }
    lastRequest = Date.now()
    
    // Call the QuoterV4 contract with the correct parameters
    const data = await publicClient.readContract({
      address: config.quoterV4Address as `0x${string}`,
      abi: QuoterV4ABI,
      functionName: 'quoteExactInputSingle',
      args: [{
        poolKey,
        exactAmount: amountInWei,
        zeroForOne: tokenIn < tokenOut ? false : true, // zeroForOne: true if tokenIn is token0
        hookData: '0x' as `0x${string}`
      }]
    }) as [bigint, bigint, number, bigint];

    const [amountOutWei, gasEstimate] = data;

    // Convert amountOut from wei to token units
    const amountOut = Number(amountOutWei) / (10 ** decimalsOut);
    
    return {
      amountIn,
      amountOut,
      tokenIn: tokenIn,
      tokenOut: tokenOut,
      fee: FeeAmount.MEDIUM, // V4 uses medium fee by default
      gasEstimate,
    };
  } catch (error) {
    // v4 pool might not exist - just continue
    //console.error(`Error getting V4 quote for ${tokenIn}/${tokenOut}:`, error);
    return null;
  }
}

/**
 * Find all possible direct pools between two tokens
 */
async function findDirectPools(
  tokenIn: THexString,
  tokenOut: THexString,
  chain: SupportedChains,
  includedPools: PoolType[],
  decimalsIn: number,
  decimalsOut: number,
  amountIn: number,
  publicClient: PublicClient

): Promise<ISwapHop[]> {
  const pools: ISwapHop[] = [];
  
  if (includedPools.includes(PoolType.V3)) {
    // Check V3 pools for all fee tiers
    for (const fee of V3_FEE_TIERS) {
      const poolAddress = await getV3PoolAddress(tokenIn, tokenOut, fee, chain, publicClient);
      
      if (poolAddress) {
        // Get pool state to check liquidity
        const poolState = await getV3PoolState(poolAddress, publicClient);
        
        if (poolState && poolState.liquidity > 1000000n) {
          // Get quote to determine price ratio
          const quote = await getV3Quote(tokenIn, tokenOut, amountIn, fee, chain, decimalsIn, decimalsOut, publicClient);

          if (quote) {
            pools.push({
              poolType: PoolType.V3,
              tokenIn,
              tokenOut,
              fee,
              poolAddress,
              amountOutPerOneIn: quote.amountOut / amountIn,
              liquidity: poolState.liquidity
            });
          }
        }
      }
    }
  }
  
  if (includedPools.includes(PoolType.V4)) {
    
    // Check V4 pool
    try {
      const quote = await getV4Quote(tokenIn, tokenOut, amountIn, chain, decimalsIn, decimalsOut, publicClient);
      if (quote) {
        pools.push({
          poolType: PoolType.V4,
          tokenIn,
          tokenOut,
          fee: FeeAmount.MEDIUM, // V4 uses medium fee by default
          amountOutPerOneIn: quote.amountOut / amountIn
        });
      }
    } catch (error) {
      // V4 pool might not exist, just continue
    }
  }
  
  return pools;
}

/**
 * Find all possible paths between two tokens with at most maxHops
 */
async function findAllPaths(
  tokenInput: TTokenInfo,
  tokenOutput: TTokenInfo,
  intermediateTokens: THexString[],
  maxHops: number,
  chain: SupportedChains,
  includedPools: PoolType[],
  amountIn: number,
  publicClient: PublicClient
): Promise<ISwapPath[]> {
  // First try direct path
  const directPools = await findDirectPools(tokenInput.address, tokenOutput.address, chain, includedPools, tokenInput.decimals, tokenOutput.decimals, amountIn, publicClient);
  
  let paths: ISwapPath[] = directPools.map(pool => ({
    hops: [pool],
    totalOutputPerInput: pool.amountOutPerOneIn || 0,
    estimatedGas: BigInt(100000) // Base gas estimate
  }));
  
  // If maxHops > 1, try paths with intermediate tokens
  if (maxHops > 1) {
    for (const intermediateToken of intermediateTokens) {
      // Skip if intermediate token is the same as input or output
      if (intermediateToken === tokenInput.address || intermediateToken === tokenOutput.address) {
        continue;
      }
      
      let intermediateTokenInfo = await getTokenInfo(publicClient, intermediateToken)
      // Find pools for first hop
      const firstHopPools = await findDirectPools(tokenInput.address, intermediateToken, chain, includedPools, tokenInput.decimals, intermediateTokenInfo.decimals, 1, publicClient);
      
      // Find pools for second hop
      const secondHopPools = await findDirectPools(intermediateToken, tokenOutput.address, chain, includedPools, intermediateTokenInfo.decimals, tokenOutput.decimals, 1, publicClient);
      
      // Create paths for all combinations of first and second hop pools
      for (const firstHop of firstHopPools) {
        for (const secondHop of secondHopPools) {
          // Calculate combined output
          const firstHopOutput = firstHop.amountOutPerOneIn || 0;
          const secondHopOutput = secondHop.amountOutPerOneIn || 0;
          const totalOutput = firstHopOutput * secondHopOutput;
          
          // Add path if both hops have valid output
          if (firstHopOutput > 0 && secondHopOutput > 0) {
            paths.push({
              hops: [firstHop, secondHop],
              totalOutputPerInput: totalOutput,
              estimatedGas: BigInt(150000) // Higher gas estimate for multi-hop
            });
          }
        }
      }
    }
  }
  
  // Sort paths by total output (descending)
  paths.sort((a, b) => b.totalOutputPerInput - a.totalOutputPerInput);
  
  return paths;
}

/**
 * Calculate the best swap path between two tokens
 * @param tokenIn - Address of the input token
 * @param tokenOut - Address of the output token
 * @param amountIn - Amount of input token to swap
 * @param chain - Chain to use
 * @param options - Additional options
 * @returns The best swap path
 */
export async function calculateBestSwapPath(
  tokenInput: TTokenInfo,
  tokenOutput: TTokenInfo,
  amountIn: number,
  publicClient: PublicClient,
  options: {
    maxHops?: number;
    includedPools?: PoolType[];
    gasPrice?: number;
  } = {}
): Promise<ISwapPath | null> {

  let chain = getSupportedChain(publicClient.chain?.id || 0)

  if (!chain) {
    throw 'Chain id is unsupported for uniswap ' + publicClient.chain?.id
  }
  
  // Set default options
  const maxHops = options.maxHops || 2;
  const includedPools = options.includedPools || [PoolType.V3, PoolType.V4];
  const gasPrice = options.gasPrice || 20; // gwei
  
  const config = CHAIN_CONFIG[chain];
  
  // Common intermediate tokens to consider for multi-hop paths
  const intermediateTokens: THexString[] = [
    config.weth_address, // WETH is a common intermediate
    // Add other common tokens like USDC, USDT, DAI, etc.
  ];
  
  // Find all possible paths
  const paths = await findAllPaths(
    tokenInput,
    tokenOutput,
    intermediateTokens,
    maxHops,
    chain,
    includedPools,
    amountIn,
    publicClient
  );
  
  if (paths.length === 0) {
    console.log(`No valid paths found between ${tokenInput.symbol} and ${tokenOutput.symbol}`);
    return null;
  }
  
  // Calculate effective output considering gas costs
  const pathsWithEffectiveOutput = paths.map(path => {
    // Calculate gas cost in terms of output token
    // This is a simplification and would need price feeds in a real implementation
    const gasCostInWei = path.estimatedGas * BigInt(gasPrice * 10**9);
    const gasCostInOutputToken = 0; // Would need price conversion in real implementation
    
    // Calculate effective output
    const effectiveOutput = (path.totalOutputPerInput * amountIn) - gasCostInOutputToken;
    
    return {
      ...path,
      effectiveOutput
    };
  });

  
  
  // Sort by effective output
  pathsWithEffectiveOutput.sort((a, b) => b.effectiveOutput - a.effectiveOutput);
  
  // Return the best path
  return pathsWithEffectiveOutput[0] || null;
}

/**
 * Encode a swap path for use with the router
 * @param path - The swap path to encode
 * @returns Encoded path for router
 */
export function encodeSwapPath(path: ISwapPath): THexString {
  if (path.hops.length === 1) {
    // Direct path
    const hop = path.hops[0];
    
    if (hop.poolType === PoolType.V3) {
      return encodePacked(
        ['address', 'uint24', 'address'],
        [hop.tokenIn, hop.fee || FeeAmount.MEDIUM, hop.tokenOut]
      ) as THexString;
    } else {
      // For V2 or V4, just encode the token addresses
      return encodePacked(
        ['address', 'address'],
        [hop.tokenIn, hop.tokenOut]
      ) as THexString;
    }
  } else if (path.hops.length === 2) {
    // Two-hop path
    const hop1 = path.hops[0];
    const hop2 = path.hops[1];
    
    if (hop1.poolType === PoolType.V3 && hop2.poolType === PoolType.V3) {
      return encodePacked(
        ['address', 'uint24', 'address', 'uint24', 'address'],
        [
          hop1.tokenIn,
          hop1.fee || FeeAmount.MEDIUM,
          hop1.tokenOut,
          hop2.fee || FeeAmount.MEDIUM,
          hop2.tokenOut
        ]
      ) as THexString;
    } else {
      // Mixed pool types would need special handling
      throw new Error('Mixed pool types in multi-hop path not supported');
    }
  } else {
    throw new Error('Paths with more than 2 hops not supported');
  }
}
